<script lang="ts">
  import { MangroveReviews, Review } from "mangrove-reviews-typescript"

  import { ImmutableStore, Store, UIEventSource } from "../../Logic/UIEventSource"
  import StarsBar from "./StarsBar.svelte"
  import Translations from "../i18n/Translations"
  import Tr from "../Base/Tr.svelte"
  import { ariaLabel } from "../../Utils/ariaLabel"
  import ThemeViewState from "../../Models/ThemeViewState"
  import Markdown from "../Base/Markdown.svelte"
  import AttributedImage from "../Image/AttributedImage.svelte"
  import DotMenu from "../Base/DotMenu.svelte"
  import { TrashIcon } from "@rgossiaux/svelte-heroicons/solid"
  import Popup from "../Base/Popup.svelte"
  import NextButton from "../Base/NextButton.svelte"
  import { PencilIcon } from "@babeard/svelte-heroicons/solid"
  import ReviewForm from "./ReviewForm.svelte"
  import type { Feature } from "geojson"
  import LayerConfig from "../../Models/ThemeConfig/LayerConfig"
  import { MangroveIdentity } from "../../Logic/Web/MangroveReviews"
  import type { ReviewCollection } from "../../Logic/Web/MangroveReviews"

  import ShieldExclamation from "@babeard/svelte-heroicons/solid/ShieldExclamation"
  import Delete_icon from "../../assets/svg/Delete_icon.svelte"
  import ValidatedInput from "../InputElement/ValidatedInput.svelte"
  import type { MapProperties } from "../../Models/MapProperties"

  export let state: ThemeViewState & {
    mapProperties?: MapProperties
    userRelatedState?: { mangroveIdentity: MangroveIdentity }
  } = undefined
  export let tags: UIEventSource<Record<string, string>> = undefined
  export let feature: Feature = undefined
  export let layer: LayerConfig = undefined
  export let reviews: ReviewCollection
  export let showMenu: boolean

  export let review: Review & {
    kid: string
    signature: string
    madeByLoggedInUser?: Store<boolean>
  }
  let name = review.metadata.nickname
  name ??= ((review.metadata.given_name ?? "") + " " + (review.metadata.family_name ?? "")).trim()
  let d = new Date()
  d.setTime(review.iat * 1000)
  let date = d.toDateString()
  let byLoggedInUser = review.madeByLoggedInUser ?? ImmutableStore.FALSE

  export let showSub = false
  let subUrl = new URL(review.sub)
  let [lat, lon] = subUrl.pathname.split(",").map((l) => Number(l))
  let sub = subUrl.searchParams.get("q")

  function selectFeature() {
    console.log("Selecting and zooming to", { lon, lat })
    state?.mapProperties?.location?.setData({ lon, lat })
    state?.mapProperties?.zoom?.setData(Math.max(16, state?.mapProperties?.zoom?.data))

    state?.guistate?.closeAll()
  }

  let isTesting = state?.featureSwitchIsTesting ?? new ImmutableStore(false)

  async function report() {
    let reason = reportReason.data
    if (!reason) {
      reason = "This review was reported by a MapComplete user"
    }
    const identity: CryptoKeyPair = await state?.userRelatedState?.mangroveIdentity.getKeypair()
    if (!isTesting.data) {
      try {
        await MangroveReviews.reportAbuseReview(identity, review, reason)
      } catch (e) {
        console.error("Could not report review due to", e)
        await state.reportError(e)
      }
    } else {
      console.log("Not actually reporting, test mode")
    }
    showReport.set(false)
    reviews.removeReviewLocally(review)
  }

  let deleted = new UIEventSource(false)

  async function deleteR() {
    const identity: CryptoKeyPair = await state?.userRelatedState?.mangroveIdentity.getKeypair()
    console.log("Deleting review...", identity)
    if (!isTesting.data) {
      try {
        await reviews.deleteReview(review)
      } catch (e) {
        console.log("Could not delete review... ", e)
        await state.reportError(e)
      }
    } else {
      console.log("Not really deleting the review, testing")
    }
    showDelete.setData(false)
    deleted.setData(true)
  }

  let reportReason = new UIEventSource("")
  let showDelete = new UIEventSource(false)
  let showEdit = new UIEventSource(false)
  let showReport = new UIEventSource(false)
  let isDebugging = state?.featureSwitches?.featureSwitchIsDebugging
  const t = Translations.t.reviews
</script>

<Popup shown={showDelete}>
  <svelte:fragment slot="header">
    <Tr t={t.deleteTitle} />
  </svelte:fragment>

  <div class="m-8 flex items-center gap-x-4">
    <Delete_icon class="w-16" />
    <div class="low-interaction border-interactive p-4">
      <StarsBar starSize="w-4 h-4 md:w-6 md:h-6" readonly={true} score={review.rating} />
      <div class="disable-links">
        <Markdown src={review.opinion} />
      </div>
    </div>
  </div>

  <Tr t={t.deleteText} />

  <NextButton clss="primary float-right" on:click={() => deleteR()}>
    <TrashIcon class="w-12 text-red-600" />
    <Tr t={t.deleteConfirm} />
  </NextButton>
</Popup>

<Popup shown={showReport}>
  <svelte:fragment slot="header">
    <Tr t={t.reportReviewTitle} />
  </svelte:fragment>

  <div class="m-8 flex items-center gap-x-4">
    <ShieldExclamation class="w-16" />
    <div class="low-interaction border-interactive p-4">
      <StarsBar starSize="w-4 h-4 md:w-6 md:h-6" readonly={true} score={review.rating} />
      <div class="disable-links">
        <Markdown src={review.opinion} />
      </div>
    </div>
  </div>

  <h3>
    <Tr t={t.reportReason} />
  </h3>
  <div class="w-full">
    <ValidatedInput type="text" value={reportReason} />
  </div>

  <Tr t={t.deleteText} />

  <NextButton
    clss="primary float-right"
    on:click={() => {
      report()
    }}
  >
    <TrashIcon class="w-12 text-red-600" />
    <Tr t={t.reportReview} />
  </NextButton>
</Popup>

<Popup shown={showEdit}>
  <svelte:fragment slot="header">
    <Tr t={t.edit} />
  </svelte:fragment>

  <ReviewForm {tags} {feature} {layer} {state} editReview={review} {reviews} />
</Popup>

<div
  class={"low-interaction flex flex-col rounded-lg p-1 px-2" +
    ($byLoggedInUser ? "border-interactive" : "")}
>
  {#if showSub}
    <button class="as-link" on:click={() => selectFeature()}>
      <h3>{sub}</h3>
    </button>
  {/if}

  <div class="flex w-full items-center justify-between">
    <div
      tabindex="0"
      use:ariaLabel={Translations.t.reviews.rated.Subs({
        n: "" + Math.round(review.rating / 10) / 2,
      })}
    >
      <StarsBar starSize="w-4 h-4 md:w-6 md:h-6" readonly={true} score={review.rating} />
    </div>
    <div class="flex flex-wrap space-x-2">
      <a
        href={`https://mangrove.reviews/list?kid=${encodeURIComponent(review.kid)}`}
        rel="noopener"
        target="_blank"
      >
        {#if !name}
          <i>Anonymous</i>
        {:else}
          <span class="font-bold">
            {name}
          </span>
        {/if}
      </a>
      <a
        class="subtle"
        target="_blank"
        rel="noopener nofollow"
        href={`https://mangrove.reviews/list?signature=${encodeURIComponent(review.signature)}`}
      >
        {date}
      </a>
    </div>
    {#if review.signature && showMenu !== undefined}
      <div class="self-start">
        <DotMenu>
          {#if $byLoggedInUser}
            <button on:click={() => showEdit.set(true)}>
              <PencilIcon />
              <Tr t={t.edit} />
            </button>
            <button on:click={() => showDelete.set(true)}>
              <TrashIcon />
              <Tr t={t.delete} />
            </button>
          {:else}
            <button on:click={() => showReport.set(true)}>
              <ShieldExclamation />
              <Tr t={t.reportReview} />
            </button>
          {/if}
        </DotMenu>
      </div>
    {/if}
  </div>

  {#if review.opinion}
    <div class="disable-links" class:strikethrough={$deleted}>
      <Markdown src={review.opinion} />
    </div>
  {/if}

  <div class="flex items-center gap-x-2 overflow-x-auto">
    {#each review.images ?? [] as image}
      <div class="w-32 min-w-32">
        <AttributedImage image={{ url: image.src, id: image.src }} />
      </div>
    {/each}
  </div>
  {#if review.metadata.is_affiliated}
    <Tr t={Translations.t.reviews.affiliated_reviewer_warning} />
  {/if}
  {#if $isDebugging}
    <div class="subtle">
      Maresi: {review.signature}
    </div>
  {/if}
</div>
